import { useEffect, useState } from "react";
import { PhoneMockupProps } from "./sms/types";

// Small local copies of messaging.tsx's own button-normalizing helpers —
// duplicated rather than imported to avoid a circular import (messaging.tsx
// already imports WhatsappMockUp from this file).
const normalizeMockButtons = (buttons: any): any[] => {
    if (!buttons) return [];
    if (Array.isArray(buttons)) return buttons;
    if (Array.isArray(buttons.buttons)) return buttons.buttons;
    return [];
};
const getMockButtonText = (button: any): string =>
    typeof button === "string" ? button : button?.text || button?.label || "";
const getMockButtonUrl = (button: any): string =>
    typeof button === "string" ? "" : button?.url || button?.website_url || button?.value || "";
// Only a URL-type button can meaningfully take the CTA form's url override —
// a quick-reply button has nowhere to navigate to.
const isMockUrlButtonType = (button: any): boolean => button?.type === "URL" || !!getMockButtonUrl(button);

// A template's own declared header_type from the backend is one of
// "image" | "video" | "document" | "file" | "text" | "none" | null.
// Normalize "file" -> "document" since that's the media kind we actually render.
const normalizeHeaderKind = (headerType?: string | null): "image" | "video" | "document" | "text" | null => {
    switch (headerType) {
        case "image": return "image";
        case "video": return "video";
        case "document":
        case "file": return "document";
        case "text": return "text";
        default: return null;
    }
};

// header_content on an existing/approved template is often an opaque
// WhatsApp media handle (e.g. "4::aW1hZ2U...:ARZC...") rather than a
// fetchable URL — only attempt to actually load it as an <img>/<video> src
// when it looks like a real URL, otherwise just show the type-specific
// placeholder icon so the marketer still knows a file belongs here even
// though it can't be previewed inline.
const looksLikeLoadableUrl = (value?: string | null): boolean =>
    !!value && /^(https?:|data:|blob:)/i.test(value);

export const WhatsappMockUp: React.FC<PhoneMockupProps> = ({
    message,
    senderName = "Darwin Stores",
    template,
    vars = {},
    cta,
    mediaFile,
    mediaUrl,
    campaignData
}) => {
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const [mediaType, setMediaType] = useState<"image" | "video" | "document" | null>(null);
    const [loadError, setLoadError] = useState(false);
    // Get the actual File object from different sources
    const getActualMediaFile = (): File | null | undefined => {
        if (mediaFile instanceof File) return mediaFile;
        if (campaignData?.mediaFile instanceof File) return campaignData.mediaFile;
        return null;
    };

    const actualMediaFile = getActualMediaFile();

    // Buttons to render — mirrors the exact same rule the actual send payload
    // uses (messaging.tsx's applyCtaOverride): the live-edited `cta` only
    // ever overlays onto the template's own FIRST button (and only when that
    // button is a URL button), never replaces the whole array — a template
    // can define 2, 3, or more buttons and every one of them must still show
    // up here, matching what will actually be sent. Only individual buttons
    // that actually carry a url get the external-link icon and become
    // clickable — a plain quick-reply button doesn't open anything.
    const templateButtons = normalizeMockButtons(template?.buttons);
    const displayButtons = templateButtons.length > 0
        ? templateButtons.map((btn, i) =>
            i === 0 && isMockUrlButtonType(btn) && cta?.label
                ? { ...(typeof btn === "object" && btn ? btn : {}), text: cta.label, url: cta.url || getMockButtonUrl(btn) }
                : btn,
          )
        : (cta?.label ? [{ text: cta.label, url: cta.url }] : []);

    const sniffMediaType = (u: string): "image" | "video" | "document" =>
        u.match(/\.(mp4|webm|ogg|mov|m4v)$/i) ? "video"
      : u.match(/\.(pdf|docx?|xlsx?|pptx?|csv)$/i) ? "document"
      : "image";

    // A template whose own declared header_type isn't a real media kind
    // (it's "text", "none", or simply not known yet) never has media, full
    // stop — none of the STRING-based sources below get to override that
    // just because e.g. `campaignData.content.header_value` happens to be
    // holding a text header's own string content (not a real url), or a
    // "none" header's payload was built with a stray leftover header_value
    // (see messaging.tsx, which collapses "text" to header_type "none" but
    // still carries the template's header_content through as header_value).
    // A genuinely fresh upload (`actualMediaFile`) is exempt — that's an
    // explicit, unambiguous action happening right now, regardless of
    // whatever header_type the template previously declared.
    const declaredHeaderKind = normalizeHeaderKind(template?.header_type);
    const isRealMediaHeaderType = declaredHeaderKind === "image" || declaredHeaderKind === "video" || declaredHeaderKind === "document";

    useEffect(() => {
        // Clean up previous blob URL if it exists and is a blob
        if (previewUrl && previewUrl.startsWith('blob:')) {
            URL.revokeObjectURL(previewUrl);
        }

        let url: string | null = null;
        let type: "image" | "video" | "document" | null = null;

        if (actualMediaFile instanceof File) {
            url = URL.createObjectURL(actualMediaFile);
            type = actualMediaFile.type.startsWith("image/") ? "image"
                 : actualMediaFile.type.startsWith("video/") ? "video"
                 : "document";
        }
        else if (!isRealMediaHeaderType) {
            // no-op — leave url/type null below.
        }
        else if (mediaUrl && mediaUrl !== "undefined" && !mediaUrl.startsWith('blob:')) {
            url = mediaUrl;
            type = sniffMediaType(url);
        }
        else if (campaignData?.mediaFileUrl && campaignData.mediaFileUrl !== "undefined" && !campaignData.mediaFileUrl.startsWith('blob:')) {
            url = campaignData.mediaFileUrl;
            type = sniffMediaType(url);
        }
        else if (campaignData?.content?.header_value && campaignData.content.header_value !== "undefined") {
            url = campaignData.content.header_value;
            type = sniffMediaType(url);
        }

        if (url && url !== "undefined") {
            setPreviewUrl(url);
            setMediaType(type);
        } else {
            setPreviewUrl(null);
            setMediaType(null);
        }

        return () => {
            if (url && url.startsWith('blob:')) {
                URL.revokeObjectURL(url);
            }
        };
    }, [isRealMediaHeaderType, actualMediaFile, mediaUrl, campaignData?.mediaFileUrl, campaignData?.content?.header_value]);

    // None of the live-override sources above produced anything — fall back
    // to what the template itself declares. This is what makes an existing/
    // approved template's own image/video/document/text header show up
    // consistently everywhere it's previewed (template creation, broadcast
    // Messaging step, Preview & Launch step) even before the marketer has
    // picked a fresh file for this particular send.
    const templateHeaderKind = declaredHeaderKind;
    const hasOverrideMedia = !!previewUrl && !!mediaType;
    const effectiveMediaType = hasOverrideMedia
        ? mediaType
        : (isRealMediaHeaderType ? templateHeaderKind : null);
    const effectiveMediaUrl = hasOverrideMedia
        ? previewUrl
        : (looksLikeLoadableUrl(template?.header_content) ? (template!.header_content as string) : null);
    const hasMedia = !!effectiveMediaType;

    // A plain text header (e.g. "Order Confirmation") — no media, just a
    // bold line above the message body, same as WhatsApp renders it for real.
    const headerText = !hasMedia && templateHeaderKind === "text" ? (template?.header_content || "") : "";

    useEffect(() => {
        setLoadError(false);
    }, [effectiveMediaUrl]);


    // Render message with colored variable highlights
    const renderMessageWithVars = () => {
        const content =
            template?.originalContent ||
            message ||
            "Preview will appear here";

        const parts = content.split(/({{[^}]+}})/g);

        return (
            <span className="text-[12px] text-gray-800 leading break-words whitespace-pre-wrap">
                {parts.map((part, i) => {
                    const match = part.match(/{{([^}]+)}}/);

                    if (!match) {
                        return <span key={i}>{part}</span>;
                    }

                    const variableKey = match[1]?.trim();

                    let value = "";

                    // Handle numbered variables {{1}}
                    if (/^\d+$/.test(variableKey)) {
                        value = vars?.[`variable_${variableKey}`] || "";
                    }

                    // Handle named variables {{today}}
                    else {
                        value = vars?.[variableKey] || "";
                    }

                    return (
                        <span
                            key={i}
                            className="inline text-[11.5px] font-medium"
                        >
                            {value ? value : part}
                        </span>
                    );
                })}
            </span>
        );
    };

    // Type-specific placeholder icon — shown whenever the template calls for
    // a header file (image, video or document) but there's nothing real to
    // load yet (no fresh upload, and the template's own header_content isn't
    // a directly loadable URL). Distinguishing the icon by type is what lets
    // the marketer tell at a glance which kind of file this template expects.
    const renderPlaceholderIcon = () => {
        if (effectiveMediaType === 'video') {
            return (
                <svg className="w-8 h-8 text-[#7B7B7B] font-semibold" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M8 5v14l11-7z" />
                </svg>
            );
        }
        if (effectiveMediaType === 'document') {
            return (
                <svg className="w-8 h-8 text-[#7B7B7B] font-semibold" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M14 2v6h6" />
                </svg>
            );
        }
        return (
            <svg className="w-8 h-8 text-[#7B7B7B] font-semibold" fill="currentColor" viewBox="0 0 24 24">
                <path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z" />
            </svg>
        );
    };

    // Render media based on type
    const renderMedia = () => {
        const canRenderReal = !!effectiveMediaUrl && !loadError;

        if (canRenderReal && effectiveMediaType === 'video') {
            return (
                <video
                    src={effectiveMediaUrl!}
                    className="w-full object-cover max-h-36"
                    controls
                    controlsList="nodownload"
                    onClick={(e) => e.stopPropagation()}
                    onError={() => setLoadError(true)}
                />
            );
        }

        if (canRenderReal && effectiveMediaType === 'image') {
            return (
                <img
                    src={effectiveMediaUrl!}
                    alt="media"
                    className="w-full object-cover max-h-36"
                    onError={() => setLoadError(true)}
                />
            );
        }

        // Document headers (and any image/video without a real loadable URL
        // yet) — a type-specific placeholder, not a broken embed.
        return (
            <div className="w-full h-28 bg-gray-200 flex items-center justify-center">
                {renderPlaceholderIcon()}
            </div>
        );
    };

    return (
        <div className="mx-auto w-full max-w-[290px]">
            <div className="relative bg-[#F6F6F6] rounded-[40px] overflow-hidden border-[8px] border-[#F6F6F6] shadow-2xl">
                {/* Status bar */}
                <div className="h-7 bg-[#F6F6F6] shadow flex items-center justify-between px-4 relative">
                    <div className="absolute inset-x-0 flex justify-center">
                        <div className="w-16 h-[5px] bg-[#333333] rounded-full mt-2" />
                    </div>
                    <span className="text-gray-700 text-[10px] font-medium z-10">9:41</span>
                    <div className="flex items-center gap-1 z-10">
                        <span className="text-gray-700 text-[10px]">▐▐▐ WiFi ▮</span>
                    </div>
                </div>

                {/* WhatsApp header */}
                <div className="bg-[#F6F6F6] shadow px-3 py-2 flex items-center gap-2">
                    <button className="text-gray-700 text-sm">←</button>
                    <div className="w-8 h-8 rounded-full bg-[#128C7E] flex items-center justify-center border border-white/20 overflow-hidden shrink-0">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="rgba(255,255,255,0.85)">
                            <path d="M12 12c2.7 0 4.8-2.1 4.8-4.8S14.7 2.4 12 2.4 7.2 4.5 7.2 7.2 9.3 12 12 12zm0 2.4c-3.2 0-9.6 1.6-9.6 4.8v2.4h19.2v-2.4c0-3.2-6.4-4.8-9.6-4.8z" />
                        </svg>
                    </div>
                    <div className="flex-1 min-w-0">
                        <p className="text-gray-700 text-[13px] capitalize font-medium leading-tight truncate">{senderName}</p>
                        <p className="text-gray-500 text-[10px] leading-tight">online</p>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M8.65234 2.87891C10.3079 2.87891 11.6532 4.20298 11.6904 5.84961L13.5605 4.39648C14.2961 3.82478 15.3678 4.34869 15.3682 5.28027V11.2393C15.3681 12.1712 14.2962 12.6952 13.5605 12.123L11.6904 10.6689C11.6541 12.3164 10.3085 13.6406 8.65234 13.6406H3.3584C1.67976 13.6403 0.318396 12.2793 0.318359 10.6006V5.91895C0.318697 4.24049 1.67995 2.87924 3.3584 2.87891H8.65234ZM3.3584 3.83984C2.21014 3.84018 1.27963 4.77069 1.2793 5.91895V10.6006C1.27933 11.7491 2.20996 12.6803 3.3584 12.6807H8.65234C9.80107 12.6807 10.7324 11.7493 10.7324 10.6006V5.91895C10.7321 4.77048 9.80089 3.83984 8.65234 3.83984H3.3584ZM14.4082 5.28027C14.4079 5.14756 14.2554 5.07307 14.1504 5.1543L11.6934 7.06445V9.4541L14.1504 11.3652C14.2554 11.4468 14.4081 11.3723 14.4082 11.2393V5.28027Z" fill="#007AFF" />
                        </svg>
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M6.00233 4.3133L4.66905 2.81335C4.35541 2.46051 3.8611 2.29553 3.45678 2.53923C3.24231 2.66851 3.03881 2.82458 2.85058 2.99139C1.91033 3.82462 1.70884 5.17335 2.15641 6.34724C2.62508 7.57647 3.4426 9.20322 4.79961 10.5602C6.93689 12.6975 9.27961 13.7602 11.1996 13.7602C12.4127 13.7602 13.3065 13.1614 13.6286 12.4367C13.7866 12.0814 13.5829 11.6988 13.2792 11.4559L11.3592 9.91992C10.8917 9.54594 10.2275 9.54594 9.76 9.91992L9.30352 10.2851C9.0964 10.4508 8.80732 10.4725 8.58208 10.3324C7.10553 9.41411 6.38287 8.60695 5.6153 7.08023C5.50529 6.86141 5.53672 6.59885 5.68972 6.4076L6.04516 5.96329C6.43475 5.4763 6.41666 4.77942 6.00233 4.3133Z" stroke="#007AFF" strokeWidth="0.96" />
                        </svg>
                    </div>
                </div>

                {/* Chat area */}
                <div
                    className="min-h-[380px] p-2 flex flex-col gap-2"
                    style={{
                        backgroundColor: "#F9F9F9",
                        backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='40' height='40'%3E%3Crect fill='%23e5ddd5' width='40' height='40'/%3E%3Ccircle cx='20' cy='20' r='8' fill='%23d0c9c0' opacity='.2'/%3E%3C/svg%3E")`,
                    }}
                >
                    <div className="flex justify-center">
                        <span className="bg-[#F6F6F6]/90 shadow-md text-[#6b5a4e] text-[9px] px-3 py-1 rounded-lg font-medium">
                            Today
                        </span>
                    </div>

                    <div className="flex items-end gap-1 max-w-[90%]">
                        <div className="bg-white rounded-tr-2xl rounded-br-2xl rounded-bl-2xl overflow-hidden shadow-sm w-full">
                            {hasMedia && renderMedia()}
                            <div className="px-2 pt-2 pb-1">
                                {headerText && (
                                    <p className="text-[12px] font-semibold text-gray-900 break-words mb-0.5">
                                        {headerText}
                                    </p>
                                )}
                                <p className="text-[12px] text-gray-800 leading  break-words">
                                    {renderMessageWithVars()}
                                </p>
                                <p className="text-[9px] text-[#8696a0] text-right mt-1">9:41 ✓✓</p>
                            </div>
                            {displayButtons.map((btn, i) => {
                                const text = getMockButtonText(btn);
                                const url = getMockButtonUrl(btn);
                                if (!text) return null;
                                const buttonContent = (
                                    <>
                                        {url && (
                                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#00a884" strokeWidth="2.5">
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                            </svg>
                                        )}
                                        <span className="text-[10px] text-center font-medium text-[#00a884]">{text}</span>
                                    </>
                                );
                                // Only a real URL button is actually clickable — a plain
                                // quick-reply button has nowhere to navigate to.
                                return url ? (
                                    <a
                                        key={i}
                                        href={url}
                                        target="_blank"
                                        rel="noreferrer"
                                        onClick={(e) => e.stopPropagation()}
                                        className="border-t border-gray-100 w-full flex items-center justify-center gap-1.5 py-2 no-underline cursor-pointer hover:bg-gray-50"
                                    >
                                        {buttonContent}
                                    </a>
                                ) : (
                                    <div key={i} className="border-t border-gray-100 w-full flex items-center justify-center gap-1.5 py-2">
                                        {buttonContent}
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                </div>

                {/* Input bar */}
                <div className="bg-[#f0f0f0] border-t border-gray-200 px-2 py-2 flex items-center gap-2">
                    <div className="w-7 h-7 rounded-full flex items-center justify-center">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="#8696a0">
                            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm5 11h-4v4h-2v-4H7v-2h4V7h2v4h4v2z" />
                        </svg>
                    </div>
                    <div className="flex-1 bg-white rounded-full h-8 flex items-center px-3">
                        <span className="text-[10px] text-gray-300">Message</span>
                    </div>
                    <div className="w-8 h-8 rounded-full bg-[#25D366] flex items-center justify-center shrink-0">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="white">
                            <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
                        </svg>
                    </div>
                </div>

                <div className="h-5 bg-[#F6F6F6] shadow flex items-center justify-center">
                    <div className="w-20 h-1 bg-[#333333] rounded-full" />
                </div>
            </div>
        </div>
    );
};